import { createSlice } from '@reduxjs/toolkit';
import { PAGE_SIZE_A4 } from '../../../../utilities/constants/pageSizeConstants';

const PX_PER_CM = 37.7952755906; // 96 DPI
const BASE_NODE_PX = 100; // matches default node image width in CustomNode
const DEFAULT_SCALE_CM = BASE_NODE_PX / PX_PER_CM; // 4 default grid squares (100px)
const DEFAULT_LABEL_FONT_PX = 38; // medium text preset
const DEFAULT_GRID_GAP_PX = 25;
const defaultBoxToolState = () => ({
  active: false,
  anchor: null,
  requestedAt: null,
  preset: null,
});

const defaultTemplateTextToolState = () => ({
  active: false,
  requestedAt: null,
});

const defaultTemplateImageToolState = () => ({
  active: false,
  requestedAt: null,
  image: null,
});

const defaultStraightLineToolState = () => ({
  active: false,
  anchor: null,
  requestedAt: null,
  color: '#111827',
  strokeWidth: 3,
});

const defaultSymbolDrawerToolState = () => ({
  active: false,
  requestedAt: null,
});

const defaultConnectionAssistState = () => ({
  active: false,
  expectedRole: null,
  expectedRoles: [],
  originNodeId: null,
  originHandleId: null,
  hoveredNodeId: null,
  hoveredHandleId: null,
});

const createBlockId = () => `tmpl_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
const createRowId = () => `tmpl_row_${Date.now()}_${Math.floor(Math.random() * 100000)}`;

export const TEMPLATE_IMAGE_SCALE_MIN = 120;
export const TEMPLATE_IMAGE_SCALE_MAX = 2000;
export const TEMPLATE_IMAGE_SCALE_DEFAULT = 320;
export const TEMPLATE_IMAGE_ALIGNMENTS = ['left', 'center', 'right'];
export const TEMPLATE_IMAGE_RATIO_OPTIONS = ['square', 'rectangle'];
export const TEMPLATE_IMAGE_RATIO_DEFAULT = 'square';
export const TEMPLATE_TEXT_WIDTH_DEFAULT = 520;
export const TEMPLATE_TEXT_DEFAULT = '<span style="font-size:40px">Doble click para editar el texto</span>';
const TEMPLATE_IMAGE_LEGACY_PERCENT_BASE = 320;

const clampTemplateImageScale = (value, unit = 'px') => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return TEMPLATE_IMAGE_SCALE_DEFAULT;
  }
  const normalized = unit === 'percent'
    ? (numeric / 100) * TEMPLATE_IMAGE_LEGACY_PERCENT_BASE
    : numeric;
  return Math.min(
    TEMPLATE_IMAGE_SCALE_MAX,
    Math.max(TEMPLATE_IMAGE_SCALE_MIN, Math.round(normalized)),
  );
};

const normalizeTemplateImageAlign = (value) => (
  TEMPLATE_IMAGE_ALIGNMENTS.includes(value) ? value : 'center'
);

const normalizeTemplateImageRatio = (value) => (
  TEMPLATE_IMAGE_RATIO_OPTIONS.includes(value) ? value : TEMPLATE_IMAGE_RATIO_DEFAULT
);

const normalizeTemplateBlockRows = (blocks) => {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return [];
  }
  const normalized = [];
  const rowSegmentCounts = new Map();
  let index = 0;
  while (index < blocks.length) {
    const block = blocks[index];
    if (!block) {
      index += 1;
      continue;
    }
    const rawRowId = typeof block.rowId === 'string' ? block.rowId : block.id;
    const contiguousBlocks = [block];
    let lookaheadIndex = index + 1;
    while (lookaheadIndex < blocks.length) {
      const nextBlock = blocks[lookaheadIndex];
      if (!nextBlock) break;
      const nextRowId = typeof nextBlock.rowId === 'string' ? nextBlock.rowId : nextBlock.id;
      if (nextRowId !== rawRowId) {
        break;
      }
      contiguousBlocks.push(nextBlock);
      lookaheadIndex += 1;
    }

    const segmentIndex = rowSegmentCounts.get(rawRowId) || 0;
    rowSegmentCounts.set(rawRowId, segmentIndex + 1);
    const rowId = segmentIndex === 0 ? rawRowId : createRowId();
    const requestedColumns = contiguousBlocks.some((item) => item?.rowColumns === 2) ? 2 : 1;
    const rowColumns = contiguousBlocks.length >= requestedColumns ? requestedColumns : contiguousBlocks.length || 1;

    contiguousBlocks.forEach((rowBlock, rowIndex) => {
      normalized.push({
        ...rowBlock,
        rowId,
        rowColumns,
        rowIndex,
      });
    });
    index = lookaheadIndex;
  }
  return normalized;
};

const createDefaultTemplateSettings = () => ({
  pageOption: 'none',
  footerOption: 'none',
  anchor: 'right',
  footerText: '',
  blocks: [],
  columns: 1,
  selectedBlockId: null,
  marginEnabled: false,
});

const sanitizeTemplateSettings = (value) => {
  const incoming = typeof value === 'object' && value ? value : {};
  const normalizeAnchor = (value) => {
    if (value === 'left' || value === 'right') {
      return value;
    }
    if (value === 'top-left' || value === 'bottom-left') {
      return 'left';
    }
    if (value === 'top-right' || value === 'bottom-right') {
      return 'right';
    }
    return 'right';
  };

    const normalizeBlocks = (blocks, defaultColumns = 1) => {
      if (!Array.isArray(blocks)) {
        return [];
      }
      const columnFallback = defaultColumns === 2 ? 2 : 1;
      let fallbackRowPointer = 0;
      return blocks
        .map((block, index) => {
          if (!block || typeof block !== 'object') return null;
          const type = block.type === 'image' ? 'image' : 'text';
          const base = {
            id: block.id || createBlockId(),
            type,
          };
        if (type === 'image') {
          const requestedScale = Object.prototype.hasOwnProperty.call(block, 'imageScale')
            ? block.imageScale
            : TEMPLATE_IMAGE_SCALE_DEFAULT;
          const requestedUnit = block.imageScaleUnit === 'px' ? 'px' : 'percent';
          base.image = block.image?.src ? block.image : null;
          base.imageScale = clampTemplateImageScale(requestedScale, requestedUnit);
          base.imageScaleUnit = 'px';
          base.imageAlign = normalizeTemplateImageAlign(block.imageAlign);
          base.imageRatio = normalizeTemplateImageRatio(block.imageRatio);
          base.imageX = Number.isFinite(Number(block.imageX)) ? Number(block.imageX) : null;
          base.imageY = Number.isFinite(Number(block.imageY)) ? Number(block.imageY) : null;
        } else {
          base.text = typeof block.text === 'string' ? block.text : '';
          base.textX = Number.isFinite(Number(block.textX)) ? Number(block.textX) : null;
          base.textY = Number.isFinite(Number(block.textY)) ? Number(block.textY) : null;
          base.textWidth = Number.isFinite(Number(block.textWidth)) ? Number(block.textWidth) : TEMPLATE_TEXT_WIDTH_DEFAULT;
        }
          const incomingRowId = typeof block.rowId === 'string' ? block.rowId : base.id;
          const incomingRowColumns = block.rowColumns === 2 ? 2 : 1;
        const incomingRowIndex = Number.isInteger(block.rowIndex) ? block.rowIndex : 0;
        base.rowId = incomingRowId;
        base.rowColumns = incomingRowColumns;
        base.rowIndex = incomingRowIndex;

        if (!block.rowId) {
          const rowIndex = index % columnFallback;
          const rowId = `fallback_row_${fallbackRowPointer}`;
          base.rowId = rowId;
          base.rowColumns = columnFallback;
          base.rowIndex = rowIndex;
          if (rowIndex === columnFallback - 1) {
            fallbackRowPointer += 1;
          }
        }
        return base;
      })
      .filter(Boolean);
  };

  const normalizedBlocks = normalizeTemplateBlockRows(
    normalizeBlocks(incoming.blocks, incoming.columns === 2 ? 2 : 1),
  );

  return {
    pageOption: typeof incoming.pageOption === 'string' ? incoming.pageOption : 'none',
    footerOption: typeof incoming.footerOption === 'string' ? incoming.footerOption : 'none',
    anchor: normalizeAnchor(incoming.anchor),
    footerText: typeof incoming.footerText === 'string' ? incoming.footerText : '',
    blocks: normalizedBlocks,
    columns: incoming.columns === 2 ? 2 : 1,
    marginEnabled: !!incoming.marginEnabled,
    selectedBlockId: normalizedBlocks.some((block) => block.id === incoming.selectedBlockId)
      ? incoming.selectedBlockId
      : null,
  };
};

const initialState = {
  pageSize: PAGE_SIZE_A4,
  scaleCm: DEFAULT_SCALE_CM,
  alignAnchorX: null,
  labelFontPx: DEFAULT_LABEL_FONT_PX,
  symbolLabelOverrides: {},
  labelPositionEditNodeId: null,
  distributionBoardPreviewEditNodeId: null,
  lineStrokePx: 3,
  gridGapPx: DEFAULT_GRID_GAP_PX,
  gridSnapEnabled: true,
  handlesHidden: false,
  hideParameterLabelsDefault: false,
  attributeLabelSeparator: ':',
  autosaveIntervalMinutes: 5,
  highlightHandles: [],
  connectionAssist: defaultConnectionAssistState(),
  resizingBusNodeId: null,
  lastMouseFlow: { x: 0, y: 0, screenX: 0, screenY: 0, inside: false },
  spawnRequest: null,
  spawnPreview: null,
  autoConnectRequest: null,
  templateSettings: createDefaultTemplateSettings(),
  boxTool: defaultBoxToolState(),
  straightLineTool: defaultStraightLineToolState(),
  symbolDrawerTool: defaultSymbolDrawerToolState(),
  templateTextTool: defaultTemplateTextToolState(),
  templateImageTool: defaultTemplateImageToolState(),
};

const viewSlice = createSlice({
  name: 'view',
  initialState,
  reducers: {
    setPageSize(state, action) {
      state.pageSize = action.payload;
    },
    setScaleCm(state, action) {
      const val = Number(action.payload);
      if (!isNaN(val) && val > 0) {
        state.scaleCm = DEFAULT_SCALE_CM;
      }
    },
    setAlignAnchorX(state, action) {
      state.alignAnchorX = action.payload;
    },
    setLabelFontPx(state, action) {
      const v = Number(action.payload);
      if (!isNaN(v) && v > 0 && v < 200) {
        state.labelFontPx = v;
      }
    },
    setSymbolLabelOverride(state, action) {
      const { symbolKey, fieldKey, label } = action.payload || {};
      if (!symbolKey || !fieldKey) return;
      const nextLabel = typeof label === 'string' ? label.trim() : '';
      const current = state.symbolLabelOverrides?.[symbolKey] || {};
      if (!nextLabel) {
        const { [fieldKey]: _removed, ...rest } = current;
        if (Object.keys(rest).length === 0) {
          delete state.symbolLabelOverrides[symbolKey];
        } else {
          state.symbolLabelOverrides[symbolKey] = rest;
        }
        return;
      }
      state.symbolLabelOverrides = {
        ...(state.symbolLabelOverrides || {}),
        [symbolKey]: {
          ...current,
          [fieldKey]: nextLabel,
        },
      };
    },
    replaceSymbolLabelOverrides(state, action) {
      const payload = action.payload;
      state.symbolLabelOverrides = payload && typeof payload === 'object' && !Array.isArray(payload)
        ? payload
        : {};
    },
    setLabelPositionEditNodeId(state, action) {
      const nodeId = action.payload;
      state.labelPositionEditNodeId = typeof nodeId === 'string' ? nodeId : null;
    },
    setDistributionBoardPreviewEditNodeId(state, action) {
      const nodeId = action.payload;
      state.distributionBoardPreviewEditNodeId = typeof nodeId === 'string' ? nodeId : null;
    },
    setLineStrokePx(state, action) {
      const v = Number(action.payload);
      if (!isNaN(v) && v > 0 && v < 100) {
        state.lineStrokePx = v;
      }
    },
    setGridGapPx(state, action) {
      const raw = Number(action.payload);
      if (!Number.isNaN(raw) && raw >= 5 && raw <= 400) {
        state.gridGapPx = raw;
      } else {
        state.gridGapPx = DEFAULT_GRID_GAP_PX;
      }
    },
    setGridSnapEnabled(state, action) {
      state.gridSnapEnabled = !!action.payload;
    },
    setHandlesHidden(state, action) {
      state.handlesHidden = !!action.payload;
    },
    setAutosaveIntervalMinutes(state, action) {
      const value = Number(action.payload);
      if (!Number.isNaN(value) && value > 0) {
        state.autosaveIntervalMinutes = value;
      }
    },
    setHideParameterLabelsDefault(state, action) {
      state.hideParameterLabelsDefault = !!action.payload;
    },
    setAttributeLabelSeparator(state, action) {
      state.attributeLabelSeparator =
        typeof action.payload === 'string' ? action.payload : state.attributeLabelSeparator;
    },
    setHighlightHandles(state, action) {
      const arr = Array.isArray(action.payload) ? action.payload : [];
      state.highlightHandles = arr;
    },
    setConnectionAssist(state, action) {
      const payload = action.payload || {};
      const expectedRole = payload.expectedRole === 'source' || payload.expectedRole === 'target'
        ? payload.expectedRole
        : null;
      const expectedRoles = Array.isArray(payload.expectedRoles)
        ? payload.expectedRoles.filter((role) => role === 'source' || role === 'target')
        : (expectedRole ? [expectedRole] : []);
      const uniqueExpectedRoles = Array.from(new Set(expectedRoles));
      state.connectionAssist = {
        active: uniqueExpectedRoles.length > 0,
        expectedRole: expectedRole || uniqueExpectedRoles[0] || null,
        expectedRoles: uniqueExpectedRoles,
        originNodeId: typeof payload.originNodeId === 'string' ? payload.originNodeId : null,
        originHandleId: typeof payload.originHandleId === 'string' ? payload.originHandleId : null,
        hoveredNodeId: null,
        hoveredHandleId: null,
      };
    },
    setConnectionAssistHover(state, action) {
      const payload = action.payload || {};
      if (!state.connectionAssist?.active) {
        state.connectionAssist = defaultConnectionAssistState();
        return;
      }
      state.connectionAssist.hoveredNodeId = typeof payload.hoveredNodeId === 'string'
        ? payload.hoveredNodeId
        : null;
      state.connectionAssist.hoveredHandleId = typeof payload.hoveredHandleId === 'string'
        ? payload.hoveredHandleId
        : null;
    },
    clearConnectionAssist(state) {
      state.connectionAssist = defaultConnectionAssistState();
    },
    setResizingBusNodeId(state, action) {
      state.resizingBusNodeId = typeof action.payload === 'string' ? action.payload : null;
    },
    requestSpawnSymbol(state, action) {
      // payload: { data: payloadForNode }
      const id = Date.now();
      state.spawnRequest = { id, ...(action.payload || {}) };
    },
    clearSpawnRequest(state) {
      state.spawnRequest = null;
    },
    setSpawnPreview(state, action) {
      const id = Date.now();
      state.spawnPreview = { id, ...(action.payload || {}) };
    },
    clearSpawnPreview(state) {
      state.spawnPreview = null;
    },
    activateSymbolDrawerTool(state) {
      state.symbolDrawerTool = {
        active: true,
        requestedAt: Date.now(),
      };
      state.spawnPreview = null;
      state.boxTool = defaultBoxToolState();
      state.straightLineTool = defaultStraightLineToolState();
      state.templateTextTool = defaultTemplateTextToolState();
      state.templateImageTool = defaultTemplateImageToolState();
      if (state.templateSettings) {
        state.templateSettings.selectedBlockId = null;
      }
    },
    resetSymbolDrawerTool(state) {
      state.symbolDrawerTool = defaultSymbolDrawerToolState();
    },
    activateBoxTool(state, action) {
      state.boxTool = {
        active: true,
        anchor: null,
        requestedAt: Date.now(),
        preset: action.payload?.preset || null,
      };
      state.spawnPreview = null;
      state.straightLineTool = defaultStraightLineToolState();
      state.symbolDrawerTool = defaultSymbolDrawerToolState();
      state.templateTextTool = defaultTemplateTextToolState();
      state.templateImageTool = defaultTemplateImageToolState();
    },
    setBoxToolAnchor(state, action) {
      if (!state.boxTool?.active) return;
      const point = action.payload;
      if (!point || typeof point.x !== 'number' || typeof point.y !== 'number') {
        state.boxTool.anchor = null;
        return;
      }
      state.boxTool.anchor = { x: point.x, y: point.y };
    },
    resetBoxTool(state) {
      state.boxTool = defaultBoxToolState();
    },
    activateStraightLineTool(state) {
      state.straightLineTool = {
        ...(state.straightLineTool || defaultStraightLineToolState()),
        active: true,
        anchor: null,
        requestedAt: Date.now(),
      };
      state.spawnPreview = null;
      state.boxTool = defaultBoxToolState();
      state.symbolDrawerTool = defaultSymbolDrawerToolState();
      state.templateTextTool = defaultTemplateTextToolState();
      state.templateImageTool = defaultTemplateImageToolState();
      if (state.templateSettings) {
        state.templateSettings.selectedBlockId = null;
      }
    },
    setStraightLineToolAnchor(state, action) {
      if (!state.straightLineTool?.active) return;
      const point = action.payload;
      if (!point || typeof point.x !== 'number' || typeof point.y !== 'number') {
        state.straightLineTool.anchor = null;
        return;
      }
      state.straightLineTool.anchor = { x: point.x, y: point.y };
    },
    setStraightLineToolColor(state, action) {
      const value = typeof action.payload === 'string' ? action.payload.trim() : '';
      if (/^#([0-9a-fA-F]{6})$/.test(value)) {
        state.straightLineTool = {
          ...(state.straightLineTool || defaultStraightLineToolState()),
          color: value.toLowerCase(),
        };
      }
    },
    setStraightLineToolStrokeWidth(state, action) {
      const value = Number(action.payload);
      if (!Number.isNaN(value) && value >= 1 && value <= 24) {
        state.straightLineTool = {
          ...(state.straightLineTool || defaultStraightLineToolState()),
          strokeWidth: Math.round(value),
        };
      }
    },
    resetStraightLineTool(state) {
      state.straightLineTool = {
        ...(state.straightLineTool || defaultStraightLineToolState()),
        active: false,
        anchor: null,
        requestedAt: null,
      };
    },
    activateTemplateTextTool(state) {
      state.templateTextTool = {
        active: true,
        requestedAt: Date.now(),
      };
      state.spawnPreview = null;
      state.boxTool = defaultBoxToolState();
      state.straightLineTool = defaultStraightLineToolState();
      state.symbolDrawerTool = defaultSymbolDrawerToolState();
      state.templateImageTool = defaultTemplateImageToolState();
      if (state.templateSettings) {
        state.templateSettings.selectedBlockId = null;
      }
    },
    resetTemplateTextTool(state) {
      state.templateTextTool = defaultTemplateTextToolState();
    },
    activateTemplateImageTool(state, action) {
      state.templateImageTool = {
        active: true,
        requestedAt: Date.now(),
        image: action.payload?.image || null,
      };
      state.spawnPreview = null;
      state.boxTool = defaultBoxToolState();
      state.straightLineTool = defaultStraightLineToolState();
      state.symbolDrawerTool = defaultSymbolDrawerToolState();
      state.templateTextTool = defaultTemplateTextToolState();
      if (state.templateSettings) {
        state.templateSettings.selectedBlockId = null;
      }
    },
    resetTemplateImageTool(state) {
      state.templateImageTool = defaultTemplateImageToolState();
    },
    requestAutoConnect(state, action) {
      const id = Date.now();
      const nodeId = action.payload?.nodeId;
      state.autoConnectRequest = nodeId ? { id, nodeId } : null;
    },
    clearAutoConnectRequest(state) {
      state.autoConnectRequest = null;
    },
    setTemplatePageOption(state, action) {
      const value = action.payload;
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      state.templateSettings.pageOption = value;
      if (value === 'first-page' && !state.templateSettings.anchor) {
        state.templateSettings.anchor = 'right';
      }
      if (value === 'none') {
        state.templateSettings.blocks = [];
        state.templateSettings.selectedBlockId = null;
        state.templateSettings.marginEnabled = false;
      }
    },
    setTemplateFooterOption(state, action) {
      const value = action.payload;
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      state.templateSettings.footerOption = value;
    },
    setTemplateAnchor(state, action) {
      const value = action.payload;
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      state.templateSettings.anchor = typeof value === 'string' ? value : null;
    },
    setTemplateFooterText(state, action) {
      const value = action.payload;
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      state.templateSettings.footerText = typeof value === 'string' ? value : '';
    },
    setTemplateBlocks(state, action) {
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      const nextBlocks = Array.isArray(action.payload) ? action.payload : [];
      state.templateSettings.blocks = normalizeTemplateBlockRows(nextBlocks);
      if (!nextBlocks.some((block) => block.id === state.templateSettings.selectedBlockId)) {
        state.templateSettings.selectedBlockId = null;
      }
    },
    setTemplateColumns(state, action) {
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      state.templateSettings.columns = action.payload === 2 ? 2 : 1;
    },
    setTemplateMarginEnabled(state, action) {
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      state.templateSettings.marginEnabled = !!action.payload;
    },
    addTemplateBlock(state, action) {
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      const payload = action.payload || {};
      const type = payload.type === 'image' ? 'image' : 'text';
      const rowColumns = payload.rowColumns === 2 ? 2 : 1;
      const rowId = typeof payload.rowId === 'string' ? payload.rowId : createRowId();
      const rowIndex = Number.isInteger(payload.rowIndex) ? payload.rowIndex : 0;
      const block = {
        id: typeof payload.id === 'string' ? payload.id : createBlockId(),
        type,
        rowId,
        rowColumns,
        rowIndex,
      };
      if (type === 'text') {
        block.text = Object.prototype.hasOwnProperty.call(payload, 'text') ? payload.text || '' : TEMPLATE_TEXT_DEFAULT;
        block.textX = Number.isFinite(Number(payload.textX)) ? Number(payload.textX) : null;
        block.textY = Number.isFinite(Number(payload.textY)) ? Number(payload.textY) : null;
        block.textWidth = Number.isFinite(Number(payload.textWidth)) ? Number(payload.textWidth) : TEMPLATE_TEXT_WIDTH_DEFAULT;
      } else {
        block.image = payload.image || null;
        block.imageScale = clampTemplateImageScale(
          Object.prototype.hasOwnProperty.call(payload, 'imageScale')
            ? payload.imageScale
            : TEMPLATE_IMAGE_SCALE_DEFAULT,
          'px',
        );
        block.imageScaleUnit = 'px';
        block.imageAlign = normalizeTemplateImageAlign(payload.imageAlign);
        block.imageRatio = normalizeTemplateImageRatio(payload.imageRatio);
        block.imageX = Number.isFinite(Number(payload.imageX)) ? Number(payload.imageX) : null;
        block.imageY = Number.isFinite(Number(payload.imageY)) ? Number(payload.imageY) : null;
      }
      state.templateSettings.blocks.push(block);
      state.templateSettings.blocks = normalizeTemplateBlockRows(state.templateSettings.blocks);
      state.templateSettings.selectedBlockId = block.id;
    },
    updateTemplateBlock(state, action) {
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      const { id, changes } = action.payload || {};
      if (!id) return;
      const index = state.templateSettings.blocks.findIndex((block) => block.id === id);
      if (index === -1) return;
      const target = state.templateSettings.blocks[index];
      const next = { ...target };
      if (target.type === 'text' && typeof changes?.text === 'string') {
        next.text = changes.text;
      }
      if (target.type === 'text' && changes) {
        if (Object.prototype.hasOwnProperty.call(changes, 'textX')) {
          next.textX = Number.isFinite(Number(changes.textX)) ? Number(changes.textX) : target.textX ?? null;
        }
        if (Object.prototype.hasOwnProperty.call(changes, 'textY')) {
          next.textY = Number.isFinite(Number(changes.textY)) ? Number(changes.textY) : target.textY ?? null;
        }
        if (Object.prototype.hasOwnProperty.call(changes, 'textWidth')) {
          next.textWidth = Number.isFinite(Number(changes.textWidth)) ? Number(changes.textWidth) : target.textWidth ?? null;
        }
      }
      if (target.type === 'image' && changes) {
        if (Object.prototype.hasOwnProperty.call(changes, 'image')) {
          next.image = changes.image || null;
        }
        if (Object.prototype.hasOwnProperty.call(changes, 'imageScale')) {
          next.imageScale = clampTemplateImageScale(changes.imageScale, 'px');
          next.imageScaleUnit = 'px';
        }
        if (Object.prototype.hasOwnProperty.call(changes, 'imageAlign')) {
          next.imageAlign = normalizeTemplateImageAlign(changes.imageAlign);
        }
        if (Object.prototype.hasOwnProperty.call(changes, 'imageRatio')) {
          next.imageRatio = normalizeTemplateImageRatio(changes.imageRatio);
        }
        if (Object.prototype.hasOwnProperty.call(changes, 'imageX')) {
          next.imageX = Number.isFinite(Number(changes.imageX)) ? Number(changes.imageX) : target.imageX ?? null;
        }
        if (Object.prototype.hasOwnProperty.call(changes, 'imageY')) {
          next.imageY = Number.isFinite(Number(changes.imageY)) ? Number(changes.imageY) : target.imageY ?? null;
        }
      }
      state.templateSettings.blocks[index] = next;
    },
    updateTemplateBlocks(state, action) {
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      const updates = Array.isArray(action.payload) ? action.payload : [];
      if (updates.length === 0) return;
      const updatesById = new Map();
      updates.forEach((item) => {
        if (item?.id && item.changes) {
          updatesById.set(item.id, item.changes);
        }
      });
      if (updatesById.size === 0) return;
      state.templateSettings.blocks = state.templateSettings.blocks.map((target) => {
        const changes = updatesById.get(target?.id);
        if (!changes) return target;
        const next = { ...target };
        if (target.type === 'text' && typeof changes.text === 'string') {
          next.text = changes.text;
        }
        if (target.type === 'text') {
          if (Object.prototype.hasOwnProperty.call(changes, 'textX')) {
            next.textX = Number.isFinite(Number(changes.textX)) ? Number(changes.textX) : target.textX ?? null;
          }
          if (Object.prototype.hasOwnProperty.call(changes, 'textY')) {
            next.textY = Number.isFinite(Number(changes.textY)) ? Number(changes.textY) : target.textY ?? null;
          }
          if (Object.prototype.hasOwnProperty.call(changes, 'textWidth')) {
            next.textWidth = Number.isFinite(Number(changes.textWidth)) ? Number(changes.textWidth) : target.textWidth ?? null;
          }
        }
        if (target.type === 'image') {
          if (Object.prototype.hasOwnProperty.call(changes, 'image')) {
            next.image = changes.image || null;
          }
          if (Object.prototype.hasOwnProperty.call(changes, 'imageScale')) {
            next.imageScale = clampTemplateImageScale(changes.imageScale, 'px');
            next.imageScaleUnit = 'px';
          }
          if (Object.prototype.hasOwnProperty.call(changes, 'imageAlign')) {
            next.imageAlign = normalizeTemplateImageAlign(changes.imageAlign);
          }
          if (Object.prototype.hasOwnProperty.call(changes, 'imageRatio')) {
            next.imageRatio = normalizeTemplateImageRatio(changes.imageRatio);
          }
          if (Object.prototype.hasOwnProperty.call(changes, 'imageX')) {
            next.imageX = Number.isFinite(Number(changes.imageX)) ? Number(changes.imageX) : target.imageX ?? null;
          }
          if (Object.prototype.hasOwnProperty.call(changes, 'imageY')) {
            next.imageY = Number.isFinite(Number(changes.imageY)) ? Number(changes.imageY) : target.imageY ?? null;
          }
        }
        return next;
      });
    },
    duplicateTemplateBlock(state, action) {
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      const id = action.payload;
      if (!id) return;
      const index = state.templateSettings.blocks.findIndex((block) => block.id === id);
      if (index === -1) return;
      const source = state.templateSettings.blocks[index];
      const duplicate = {
        ...JSON.parse(JSON.stringify(source)),
        id: createBlockId(),
        rowId: createRowId(),
        rowColumns: 1,
        rowIndex: 0,
      };
      if (duplicate.type === 'text') {
        const textX = Number.isFinite(Number(source.textX)) ? Number(source.textX) : null;
        const textY = Number.isFinite(Number(source.textY)) ? Number(source.textY) : null;
        duplicate.textX = textX === null ? null : textX + 24;
        duplicate.textY = textY === null ? null : textY + 24;
      }
      if (duplicate.type === 'image') {
        const imageX = Number.isFinite(Number(source.imageX)) ? Number(source.imageX) : null;
        const imageY = Number.isFinite(Number(source.imageY)) ? Number(source.imageY) : null;
        duplicate.imageX = imageX === null ? null : imageX + 24;
        duplicate.imageY = imageY === null ? null : imageY + 24;
      }
      state.templateSettings.blocks.splice(index + 1, 0, duplicate);
      state.templateSettings.blocks = normalizeTemplateBlockRows(state.templateSettings.blocks);
      state.templateSettings.selectedBlockId = duplicate.id;
    },
    removeTemplateBlock(state, action) {
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      const id = action.payload;
      if (!id) return;
      const filtered = state.templateSettings.blocks.filter((block) => block.id !== id);
      state.templateSettings.blocks = normalizeTemplateBlockRows(filtered);
      if (state.templateSettings.selectedBlockId === id) {
        state.templateSettings.selectedBlockId = null;
      }
    },
    reorderTemplateBlocks(state, action) {
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      const { fromIndex, toIndex } = action.payload || {};
      const blocks = state.templateSettings.blocks;
      if (
        !Array.isArray(blocks)
        || typeof fromIndex !== 'number'
        || typeof toIndex !== 'number'
        || fromIndex < 0
        || toIndex < 0
        || fromIndex >= blocks.length
        || toIndex > blocks.length
      ) {
        return;
      }
      if (fromIndex === toIndex || (fromIndex === blocks.length - 1 && toIndex === blocks.length)) {
        return;
      }
      const [moved] = blocks.splice(fromIndex, 1);
      const insertionIndex = Math.min(Math.max(0, toIndex), blocks.length);
      blocks.splice(insertionIndex, 0, moved);
      state.templateSettings.blocks = normalizeTemplateBlockRows(blocks);
    },
    selectTemplateBlock(state, action) {
      if (!state.templateSettings) {
        state.templateSettings = createDefaultTemplateSettings();
      }
      const id = action.payload;
      if (id && !state.templateSettings.blocks.some((block) => block.id === id)) {
        state.templateSettings.selectedBlockId = null;
        return;
      }
      state.templateSettings.selectedBlockId = id || null;
    },
    replaceTemplateSettings(state, action) {
      state.templateSettings = sanitizeTemplateSettings(action.payload);
    },
    resetTemplateSettings(state) {
      state.templateSettings = createDefaultTemplateSettings();
    },
  },
});

export const {
  setPageSize,
  setScaleCm,
  setAlignAnchorX,
  setLabelFontPx,
  setSymbolLabelOverride,
  replaceSymbolLabelOverrides,
  setLabelPositionEditNodeId,
  setDistributionBoardPreviewEditNodeId,
  setLineStrokePx,
  setGridGapPx,
  setGridSnapEnabled,
  setHandlesHidden,
  setAutosaveIntervalMinutes,
  setAttributeLabelSeparator,
  setHighlightHandles,
  setConnectionAssist,
  setConnectionAssistHover,
  clearConnectionAssist,
  setResizingBusNodeId,
  requestSpawnSymbol,
  clearSpawnRequest,
  setSpawnPreview,
  clearSpawnPreview,
  activateSymbolDrawerTool,
  resetSymbolDrawerTool,
  activateBoxTool,
  setBoxToolAnchor,
  resetBoxTool,
  activateStraightLineTool,
  setStraightLineToolAnchor,
  setStraightLineToolColor,
  setStraightLineToolStrokeWidth,
  resetStraightLineTool,
  activateTemplateTextTool,
  resetTemplateTextTool,
  activateTemplateImageTool,
  resetTemplateImageTool,
  requestAutoConnect,
  clearAutoConnectRequest,
  setTemplatePageOption,
  setTemplateFooterOption,
  setTemplateAnchor,
  setTemplateFooterText,
  setTemplateBlocks,
  setTemplateColumns,
  setTemplateMarginEnabled,
  addTemplateBlock,
  updateTemplateBlock,
  updateTemplateBlocks,
  duplicateTemplateBlock,
  removeTemplateBlock,
  reorderTemplateBlocks,
  selectTemplateBlock,
  setHideParameterLabelsDefault,
  replaceTemplateSettings,
  resetTemplateSettings,
} = viewSlice.actions;
export default viewSlice.reducer;

export { sanitizeTemplateSettings };
